<!-- Hero -->
<section
  class="relative flex min-h-[calc(100svh-4.25rem)] items-center overflow-hidden bg-ink sm:min-h-[34rem]"
>
  <div class="pointer-events-none absolute inset-0" aria-hidden="true">
    <img
      src="assets/marketing/buy-hero.webp"
      alt=""
      fetchpriority="high"
      class="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
    />
    <div class="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30"></div>
    <div class="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/30 to-transparent"></div>
    <div class="hero-grain absolute inset-0 opacity-[0.05] mix-blend-overlay"></div>
  </div>

  <div class="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
    <div class="max-w-xl">
      <p class="text-sm font-medium uppercase tracking-wide text-gold">For buyers</p>
      <h1 class="mt-4 font-display text-4xl font-semibold text-cream sm:text-5xl lg:text-6xl">
        Buy with total confidence.
      </h1>
      <p class="mt-5 max-w-md text-lg text-cream/75">
        Every seller is identity-verified. Your payment is held securely until you confirm the
        saddle's as described. Not right for your horse? Keep it and resell it on Galopa — free, 0%
        commission.
      </p>

      <div class="mt-9 flex flex-wrap items-center gap-4">
        <a
          routerLink="/shop"
          class="rounded-full bg-cream px-6 py-3 text-sm font-medium text-forest transition hover:bg-tanlight"
        >
          Shop saddles
        </a>
        <a
          href="#how-it-works"
          class="rounded-full border border-cream/40 px-6 py-3 text-sm font-medium text-cream transition hover:border-cream"
        >
          See how it works
        </a>
      </div>
    </div>
  </div>

  <div
    class="absolute bottom-6 right-4 hidden max-w-[220px] rounded-2xl bg-cream p-5 text-ink shadow-2xl sm:right-8 sm:block lg:right-16"
  >
    <p class="font-display text-2xl text-forest">48 hours</p>
    <p class="mt-1 text-sm text-ink/70">to confirm it's as described once it arrives.</p>
  </div>
</section>

<!-- Trust strip -->
<section class="border-t border-cream/10 bg-forest text-cream">
  <div
    class="mx-auto grid max-w-7xl grid-cols-2 gap-x-4 gap-y-8 px-4 py-10 sm:gap-6 sm:px-6 sm:py-8 lg:grid-cols-4 lg:gap-8 lg:px-8"
  >
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="shield" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">Verified sellers</p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">ID-checked, every time</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="clock" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">48-hour inspection</p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Confirm it's as described</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="lock" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">Secure payments</p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Funds held until you're sure</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="check" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">Fair disputes</p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Timestamped photos protect you</p>
      </div>
    </div>
  </div>
</section>

<!-- How it works -->
<section id="how-it-works" class="mx-auto max-w-5xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <p class="mb-3 text-center text-sm font-medium uppercase tracking-wide text-tandark">
    How it works
  </p>
  <h2 class="text-center font-display text-3xl font-semibold text-ink sm:text-4xl">
    From search to saddle, in three steps
  </h2>

  <div class="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-3 sm:gap-8">
    <div class="flex flex-col items-center text-center sm:items-start sm:text-left">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-display font-semibold text-cream"
        >1</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">Search & compare</p>
      <p class="text-sm text-ink/70">
        Filter by discipline, seat size, tree width and budget until you find the one.
      </p>
    </div>
    <div class="flex flex-col items-center text-center sm:items-start sm:text-left">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-display font-semibold text-cream"
        >2</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">Message or make an offer</p>
      <p class="text-sm text-ink/70">
        Ask the seller questions, then buy now or negotiate — all on Galopa, no email
        back-and-forth.
      </p>
    </div>
    <div class="flex flex-col items-center text-center sm:items-start sm:text-left">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-display font-semibold text-cream"
        >3</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">Confirm within 48 hours</p>
      <p class="text-sm text-ink/70">
        Your card is only charged once the seller accepts. Once it arrives, confirm it's as
        described — or do nothing and it's automatically yours.
      </p>
    </div>
  </div>
</section>

<!-- Cross-sell: Wanted Saddle -->
<section class="px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <div
    class="mx-auto grid max-w-6xl grid-cols-1 items-center overflow-hidden rounded-3xl border border-line bg-tanlight/30 lg:grid-cols-2"
  >
    <div class="p-8 sm:p-12">
      <p class="text-sm font-medium uppercase tracking-wide text-tandark">Can't find it?</p>
      <h2 class="mt-3 font-display text-2xl font-semibold text-ink sm:text-3xl">
        Tell us what you're after instead.
      </h2>
      <p class="mt-4 text-ink/70">
        Post a Wanted Saddle — brand, size, tree width, budget — and sellers with a matching saddle
        already listed can respond directly.
      </p>
      <a
        routerLink="/want-a-saddle"
        class="mt-7 inline-flex items-center gap-1.5 rounded-full bg-ink px-6 py-3 text-sm font-medium text-cream transition hover:opacity-90"
      >
        Explore Wanted Saddle
        <app-icon name="arrowRight" svgClass="h-4 w-4" [strokeWidth]="2" />
      </a>
    </div>
    <div class="order-first aspect-[4/3] overflow-hidden lg:order-last lg:aspect-auto lg:h-full">
      <img
        src="assets/marketing/tackroom-panel.webp"
        alt="A saddle resting on a rack in a tack room"
        loading="lazy"
        class="h-full w-full object-cover"
      />
    </div>
  </div>
</section>

<!-- Final CTA -->
<section class="bg-creamdark/60 py-20 sm:py-24">
  <div class="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
    <h2 class="font-display text-3xl font-semibold text-ink sm:text-4xl">
      Ready to find your next saddle?
    </h2>
    <p class="mt-4 text-ink/70">Free to browse and message. You only pay once you buy.</p>
    <a
      routerLink="/shop"
      class="mt-8 inline-block rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition hover:bg-forestdark"
    >
      Shop saddles
    </a>
  </div>
</section>
